/**
 * Vendor-neutral connection interfaces (plan §6.1). The core never names a vendor:
 * adapters in the server translate a vendor's API into these shapes. Raw payloads are
 * not part of these types; adapters store the verbatim wire text separately (lesson 15).
 * @module
 */
import { z } from 'zod';
import { centsSchema } from '../money/index.js';
import { nonNegativeQtyString, qtyString } from '../quantity/index.js';

const isoInstant = z.iso.datetime({ offset: true });
const ref = z.string().min(1);

/** What a connector can do. */
export const CONNECTOR_CAPABILITIES = ['catalogue_import', 'sales_source'] as const;
/** One of {@link CONNECTOR_CAPABILITIES}. */
export type ConnectorCapability = (typeof CONNECTOR_CAPABILITIES)[number];

/** How a connector presents itself. The display name comes from here, never from UI code. */
export interface ConnectorDescriptor {
  /** Stable machine key, e.g. `pos_x_store`. */
  kind: string;
  /** Shown to users, e.g. "Switch from X". */
  displayName: string;
  capabilities: readonly ConnectorCapability[];
}

/**
 * Result of the target-identity guard (lesson 16): the connector asks the source who it
 * is and compares with the identity the owner authorised. Never an environment label.
 */
export interface TargetCheck {
  ok: boolean;
  /** Identity the source reported, or null if it could not be read. */
  actualIdentity: string | null;
}

/** Common to every connector. */
export interface Connector {
  readonly descriptor: ConnectorDescriptor;
  /** Must be called, and must pass, before any extract or fetch. */
  verifyTarget(expectedIdentity: string): Promise<TargetCheck>;
}

// ---------------------------------------------------------------------------------------
// Catalogue import

/** A component of a composite (recipe-like) source item. */
export const sourceComponentSchema = z.object({
  sourceItemRef: ref,
  sourceVariantRef: ref.optional(),
  /** Decimal string in the component's own unit. */
  qty: qtyString,
});

/** A per-store price for a variant. */
export const sourceStorePriceSchema = z.object({
  storeRef: ref,
  priceCents: centsSchema.optional(),
  available: z.boolean(),
});

/** A sellable variant (size, flavour…) of a source item. */
export const sourceVariantSchema = z.object({
  sourceId: ref,
  sku: z.string().optional(),
  barcode: z.string().optional(),
  /** Variant name if the source has one. */
  name: z.string().optional(),
  /** Option values, e.g. `["Large", "Oat milk"]`. */
  options: z.array(z.string()),
  /** Default price. */
  priceCents: centsSchema.optional(),
  costCents: centsSchema.optional(),
  /**
   * What the item's primary supplier charges for one unit (VAT-exclusive), when the
   * source records it. Becomes that supplier's imported price (M6).
   */
  purchaseCostCents: centsSchema.optional(),
  storePrices: z.array(sourceStorePriceSchema).optional(),
  /**
   * Set when the source deleted this variant while its item lives on. Adapters pass such
   * variants through (never drop them), so the plan accounts for them with a reason.
   */
  deletedAt: isoInstant.optional(),
});

/** An item as the source system holds it. */
export const sourceItemSchema = z.object({
  sourceId: ref,
  name: z.string(),
  sku: z.string().optional(),
  barcode: z.string().optional(),
  categoryRef: ref.optional(),
  soldByWeight: z.boolean(),
  /** Made of other items (a source-side recipe). */
  isComposite: z.boolean(),
  /** The source's "use production" setting. */
  useProduction: z.boolean(),
  components: z.array(sourceComponentSchema),
  variants: z.array(sourceVariantSchema),
  trackStock: z.boolean(),
  /** The supplier the item is usually bought from (a `SourceSupplier.sourceId`). */
  primarySupplierRef: ref.optional(),
  deletedAt: isoInstant.optional(),
});

/** A category (reporting only: roles never come from category names, lesson 5). */
export const sourceCategorySchema = z.object({
  sourceId: ref,
  name: z.string(),
  deletedAt: isoInstant.optional(),
});

/** A supplier: **business** contact details only, never personal data. */
export const sourceSupplierSchema = z.object({
  sourceId: ref,
  name: z.string(),
  contactName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  address: z.string().optional(),
  note: z.string().optional(),
  deletedAt: isoInstant.optional(),
});

/** A store/outlet in the source system. */
export const sourceStoreSchema = z.object({
  sourceId: ref,
  name: z.string(),
  deletedAt: isoInstant.optional(),
});

/** A stock level the source reports (observed, never truth). */
export const sourceInventoryLevelSchema = z.object({
  sourceItemRef: ref,
  sourceVariantRef: ref.optional(),
  storeRef: ref,
  qty: qtyString,
});

/** Everything a catalogue import delivers into the migration pipeline. */
export const sourceCatalogueSchema = z.object({
  /** Which connector, which target, when. */
  meta: z.object({
    connectorKind: ref,
    targetIdentity: ref,
    extractedAt: isoInstant,
  }),
  items: z.array(sourceItemSchema),
  categories: z.array(sourceCategorySchema),
  suppliers: z.array(sourceSupplierSchema),
  stores: z.array(sourceStoreSchema),
  inventoryLevels: z.array(sourceInventoryLevelSchema),
});

/** A source catalogue. */
export type SourceCatalogue = z.infer<typeof sourceCatalogueSchema>;
/** A source item. */
export type SourceItem = z.infer<typeof sourceItemSchema>;
/** A source variant. */
export type SourceVariant = z.infer<typeof sourceVariantSchema>;
/** A source component. */
export type SourceComponent = z.infer<typeof sourceComponentSchema>;
/** A source category. */
export type SourceCategory = z.infer<typeof sourceCategorySchema>;
/** A source supplier. */
export type SourceSupplier = z.infer<typeof sourceSupplierSchema>;
/** A source store. */
export type SourceStore = z.infer<typeof sourceStoreSchema>;
/** A source inventory level. */
export type SourceInventoryLevel = z.infer<typeof sourceInventoryLevelSchema>;

/** Options for {@link CatalogueImport.extract}. */
export interface ExtractOptions {
  signal?: AbortSignal;
  /** Called as pages arrive (for a progress bar); counts only. */
  onProgress?: (progress: { stage: string; done: number; total?: number }) => void;
  /**
   * Called when the source asks us to slow down (`rate_limited`) or fails briefly
   * (`source_unavailable`) and the next request waits `ms` before it is retried.
   */
  onWait?: (wait: { reason: 'rate_limited' | 'source_unavailable'; ms: number }) => void;
}

/** Delivers a source catalogue (plan §6.1, §12). */
export interface CatalogueImport extends Connector {
  extract(options?: ExtractOptions): Promise<SourceCatalogue>;
}

// ---------------------------------------------------------------------------------------
// Sales source

/** A tax on a sale line. */
export const saleTaxSchema = z.object({
  code: ref,
  /** Rate in basis points (15% = 1500). */
  rateBp: z.int().min(0),
  amountCents: centsSchema,
  /** True when the line price already includes this tax. */
  included: z.boolean(),
});

/** One line of a sale or refund. Quantities are never negative: `kind` carries the sign. */
export const saleLineSchema = z.object({
  /** Stable line id within the sale (idempotency, lesson 2). */
  lineRef: ref,
  sourceItemRef: ref,
  sourceVariantRef: ref.optional(),
  qty: nonNegativeQtyString,
  unitPriceCents: centsSchema,
  grossCents: centsSchema,
  discountCents: centsSchema,
  taxes: z.array(saleTaxSchema),
});

/** Payment kinds, normalised. `label` keeps the source's own name. */
export const PAYMENT_TYPES = ['cash', 'card', 'eft', 'voucher', 'other'] as const;

/** A payment (tender) on a sale; a ticket can have several. */
export const salePaymentSchema = z.object({
  type: z.enum(PAYMENT_TYPES),
  label: z.string().optional(),
  amountCents: centsSchema,
});

/**
 * Order types a sale can carry. The VAT module's `ORDER_TYPES` has the same values (it
 * cannot be imported here without a cycle; connections.test.ts proves they stay equal).
 */
export const SALE_ORDER_TYPES = ['takeaway', 'eat_in', 'delivery'] as const;

/**
 * A sale that reached its source more than this after it happened was made offline and
 * is **late** (D-038): adapters set `sourceCreatedAt` only then (seconds of till-to-server
 * latency are not late), and the replay counts it. The one definition (review CR2-5).
 */
export const LATE_RECEIPT_AFTER_MS = 60_000;

/** True when a sale recorded at `recordedAt` for a sale at `occurredAt` is late. */
export function isLateReceipt(occurredAt: string, recordedAt: string): boolean {
  return Date.parse(recordedAt) - Date.parse(occurredAt) > LATE_RECEIPT_AFTER_MS;
}

/** A sale, refund or cancellation from any channel, normalised. */
export const normalizedSaleSchema = z
  .object({
    /** Which channel (ours, not the vendor's name). */
    channelRef: ref,
    /** The sale's id in the source (receipt number, order id). */
    externalId: ref,
    /** Increases when the source changes the sale (idempotency key includes it). */
    revision: z.int().min(0),
    kind: z.enum(['sale', 'refund']),
    /** For a refund: the sale it refunds. */
    refundForExternalId: ref.optional(),
    /** Set when the source cancelled the sale; movements are reversed. */
    cancelledAt: isoInstant.optional(),
    /** When the sale happened (trading day comes from this, in the account zone). */
    occurredAt: isoInstant,
    /** When the source recorded it, if different (late offline sales). */
    sourceCreatedAt: isoInstant.optional(),
    /**
     * How the customer took it, when the source says (VAT: zero-rated food eaten in is
     * standard-rated). Same values as `ORDER_TYPES` in `@enp/domain/vat`.
     */
    orderType: z.enum(SALE_ORDER_TYPES).optional(),
    lines: z.array(saleLineSchema),
    payments: z.array(salePaymentSchema),
  })
  .refine((s) => s.kind === 'refund' || s.refundForExternalId === undefined, {
    message: 'Only a refund can refer to another sale',
    path: ['refundForExternalId'],
  });

/** A normalised sale. */
export type NormalizedSale = z.infer<typeof normalizedSaleSchema>;
/** A normalised sale line. */
export type SaleLine = z.infer<typeof saleLineSchema>;
/** A tax on a sale line. */
export type SaleTax = z.infer<typeof saleTaxSchema>;
/** A payment on a sale. */
export type SalePayment = z.infer<typeof salePaymentSchema>;

/** Options for {@link SalesSource.fetchSales}. */
export interface FetchSalesOptions {
  /** Inclusive start (ISO instant). */
  from: string;
  /** Exclusive end (ISO instant). */
  to: string;
  /** Resume point from an earlier `onCursor`. */
  cursor?: string;
  signal?: AbortSignal;
  /**
   * Called with a resume cursor after everything before it has been yielded, so a caller
   * can persist progress and pass it back as `cursor`.
   */
  onCursor?: (cursor: string) => void;
}

/** Delivers normalised sales, refunds and cancellations, oldest first (plan §6.1). */
export interface SalesSource extends Connector {
  fetchSales(options: FetchSalesOptions): AsyncIterable<NormalizedSale>;
}
